import * as React from 'react';
import {Dialog, DialogActions, DialogContent, DialogTitle, ThemeProvider, Typography} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import {useState} from "react";
import {createRoot} from "react-dom/client";
import {
  useAgentAssignedToClientLazyQuery,
  useClientGroupByClientIdQuery, useSaveExpectedCloseDateMutation,
  useSaveStatusMutation
} from '../api';
import { getUserName } from '@coverright/utils';
import { keycloak } from '@coverright/shared/keycloak';
import { theme } from '../../app/theme';
import { enrollmentApolloClient } from '@coverright/data-access/apollo-clients';
import { StatusDto } from '@coverright/data-access/types/enrollment';

type Props = {
  open: boolean;
  onClose: (result: boolean) => void;
  loading: boolean;
  names: string[];
};

const Modal = (props: Props) => {
  const profileStr = props.names.length > 1 ? 'are profiles' : 'is a profile'

  return (
    <Dialog
      open={props.open}
      maxWidth={'sm'}
      id={'FollowUpDateGroupModal-modal'}
    >
      <DialogTitle variant={'h3'}>
        There {profileStr} tied to this client:
      </DialogTitle>
      <DialogContent>
        <ol>
          {props.names.map(name => <li className={'fs-18'}>{name}</li>)}
        </ol>

        <Typography fontWeight={500}>
         Do you want to update the Next contact date and status for that {profileStr.split(' ').at(-1)} too?
        </Typography>

      </DialogContent>
      <DialogActions>
        <LoadingButton variant={'outlined'}
                       loading={props.loading}
                       className={'mr-16'}
                       onClick={() => {props.onClose(false)}}>
          No
        </LoadingButton>
        <LoadingButton variant={'contained'}
                       loading={props.loading}
                       color={'primary'}
                       onClick={() => {props.onClose(true)}}>
          Yes
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

interface FollowUpDateGroupModalProps {
  clientId: string,
  followUpDate: string,
  expectedCloseDate?: string,
  agentId?: string,
  status: StatusDto,
  onClose: () => void
}

const FollowUpDateGroupModal = ({clientId, followUpDate, expectedCloseDate, status, agentId, onClose}: FollowUpDateGroupModalProps) => {
  const {data} = useClientGroupByClientIdQuery({
    variables: {clientId},
    skip: !clientId,
    client: enrollmentApolloClient
  });
  const [saveStatus] = useSaveStatusMutation({client: enrollmentApolloClient});
  const [saveExpectedCloseDate] = useSaveExpectedCloseDateMutation({client: enrollmentApolloClient});
  const [getAgent] = useAgentAssignedToClientLazyQuery({client: enrollmentApolloClient})
  const [loading, setLoading] = useState(false)

  const clients = data?.clientGroupByClientId?.clients || [];

  if (data?.clientGroupByClientId === null) {
    onClose();
  }

  const closeHandle = async (result: boolean) => {
    if (result) {
      setLoading(true);
      for (const client of clients) {
        const agent = await getAgent({variables: {clientId: client.clientId}}).then(res => res.data?.agentAssignedToClient);
        await saveStatus({
          variables: {
            id: client.clientId,
            followUpDate,
            status,
            agentId: agent?.id ? agent.id : agentId ? agentId : keycloak?.tokenParsed?.sub,
          }
        })
        if (expectedCloseDate) {
          await saveExpectedCloseDate({
            variables: {
              id: client.clientId,
              expectedCloseDate,
            }
          })
        }
      }
    }
    onClose();
  }

  return <Modal loading={loading}
                names={clients.filter(c => c.clientId !== clientId).map(c => getUserName(c)) || []}
                open={clients.length > 1}
                onClose={closeHandle} />
}

export function useFollowUpDateGroupModal() {
  return (clientId: string, followUpDate: string, status: StatusDto, agentId?: string, expectedCloseDate?: string): Promise<void> => {
    return new Promise((resolve) => {
      const container = getContainer();
      const root = createRoot(container);
      root.render(
        <ThemeProvider theme={theme}>
          <FollowUpDateGroupModal clientId={clientId}
                                  followUpDate={followUpDate}
                                  expectedCloseDate={expectedCloseDate}
                                  status={status}
                                  agentId={agentId}
                                  onClose={() => {
                                    root.unmount()
                                    document.body.removeChild(container);
                                    resolve()
                                  }}
          />
        </ThemeProvider>
      )
    })
  }
}

const getContainer = () => {
  const id = 'FollowUpDateGroupModal-container'
  const prev = document.getElementById(id);
  if (prev) {
    document.body.removeChild(prev);
  }
  const modal = document.getElementById('FollowUpDateGroupModal-modal');
  if (modal) {
    document.body.removeChild(modal);
  }
  const div = document.createElement('div');
  div.id = id;
  document.body.appendChild(div);
  return div;
}
